/**
 * IntegratedSidebarNav.jsx — 작업 대상 "통합관리"일 때의 사이드바 메뉴 (2026-10-01, 시안대로)
 *
 *   [통합 관리] 제목 — 2026-10-06: 바로 위 '내 정보'(작업 대상과 무관하게 고정)와 확실히 떨어져 보이게
 *     다른 묶음 제목(9px)보다 크고 진하게 붙인다. 없을 때 내 출퇴근·내 연차가 통합관리 메뉴처럼 보였다.
 *   통합 관리 홈
 *   [분류별 통합 메뉴] — 같은 기능의 일반 메뉴가 놓인 분류를 따라간다(직원·근태 / 콜·영업 …), 이름 앞 "*" 는 뺀다
 *   관리 › 통합 메뉴 관리 — 운영자만
 *
 * 짝이 있는 출퇴근 달력 메뉴는 그 출퇴근 메뉴 안 [달력] 탭이라 뺀다(sidebarIntegratedMenus).
 * 회사 표시: PC 는 회사 색 칸(P·D·L), 휴대폰은 회색 글자 "PINE·DOT".
 * 통합 메뉴·통합 관리 홈도 휴대폰 상단 바로가기에 넣을 수 있다(2026-10-24 사용자 신고 — 전엔 상단 바가 주소를 못 찾아 막았다.
 *   이제 shortcutHelpers.resolveShortcut 이 지금 보이는 통합 메뉴 목록에서 찾는다).
 */
import React from 'react';
import { Layers, Settings } from 'lucide-react';
import MenuItem from './MenuItem';
import {
  INTEGRATED_HOME_PATH, INTEGRATED_MANAGE_PATH, groupIntegratedMenus, companyBadges, companyBadgeText,
} from './integratedScope';
import { sidebarIntegratedMenus } from '../attendancehub/integratedAttendanceTabs';
import { companyColor } from '../integratedmenu/integratedFeatureMeta';
import { useCompanyNames } from './useCompanyNames';

const groupLabelCls = 'text-[9px] font-black text-[var(--text-muted)] uppercase tracking-[0.08em] px-2 pt-2 pb-1';
// 통합 메뉴 구역 전체 제목 — 묶음 제목보다 한 단계 크게(사이드바 아래쪽 '내 정보'와 구분)
const sectionTitleCls = 'text-[11px] font-black text-[var(--text-sub)] tracking-[0.04em] px-2 pt-1.5 pb-1';

// 회사 표시 = 작업 대상과 같은 이름(2026-10-15) — PC 는 이름 첫 글자(유·무 / 파·D·L), 휴대폰은 이름 전체
function CompanyMarks({ codes, hqCode, isMobile, nameOf }) {
  if (isMobile) {
    return <span className="text-[11px] font-semibold text-[var(--text-muted)]">{companyBadgeText(codes, hqCode, nameOf)}</span>;
  }
  return companyBadges(codes, hqCode, nameOf).map((b) => {
    const col = companyColor(b.code);
    return (
      <span
        key={b.code}
        title={b.name}
        className="rounded px-[3px] py-px text-[8px] font-black leading-none"
        style={{ background: col.bg, color: col.fg }}
      >
        {b.short}
      </span>
    );
  });
}

/**
 * @param {Array} menus        이 사람에게 보일 통합 메뉴(useCompanyScope().integratedMenus)
 * @param {Array} menuGroups   분류가 적용된 일반 메뉴 묶음(composeAdminMenus().menuGroups) — 분류·아이콘 재료
 * @param {string[]} dbOrder   DB 분류 순서(useMenuCategories().order)
 */
// canManage: 운영자 또는 '통합 메뉴 관리' 열쇠(테넌트 관리자 자동, 2026-10-15) — 맨 아래 '관리 > 통합 메뉴 관리' 칸
export default function IntegratedSidebarNav({
  menus, menuGroups, dbOrder, hqCode, pathname, canManage, isMobile, onNavigate,
}) {
  const groups = groupIntegratedMenus(sidebarIntegratedMenus(menus), menuGroups, dbOrder);
  const nameOf = useCompanyNames();
  return (
    <>
      <div className="flex flex-col gap-[1px] pb-1">
        <div className={sectionTitleCls}>통합 관리</div>
        <MenuItem
          to={INTEGRATED_HOME_PATH}
          label="통합 관리 홈"
          Icon={Layers}
          isActive={pathname === INTEGRATED_HOME_PATH}
          onClick={onNavigate}
        />
      </div>
      {groups.map((g) => (
        <div key={g.group} className="flex flex-col gap-[1px] pb-1">
          <div className={groupLabelCls}>{g.group}</div>
          {g.items.map((it) => (
            <MenuItem
              key={it.to}
              to={it.to}
              label={it.label}
              Icon={it.Icon || Layers}
              isActive={pathname === it.to}
              onClick={onNavigate}
              badges={<CompanyMarks codes={it.menu.company_codes} hqCode={hqCode} isMobile={isMobile} nameOf={nameOf} />}
            />
          ))}
        </div>
      ))}
      {canManage && (
        <div className="flex flex-col gap-[1px] pb-1 mt-1.5 pt-1 border-t border-[var(--border)]">
          <div className={groupLabelCls}>관리</div>
          <MenuItem
            to={INTEGRATED_MANAGE_PATH}
            label="통합 메뉴 관리"
            Icon={Settings}
            isActive={pathname === INTEGRATED_MANAGE_PATH}
            onClick={onNavigate}
          />
        </div>
      )}
    </>
  );
}
